/**
 * Developer MCP grant scopes.
 *
 * Scopes narrow, never widen. A grant cannot reach a tool the workspace has
 * blocked, cannot exceed the member's role, and cannot outlive their
 * membership. What a scope decides is which of the tools they could already
 * use this particular third-party client is allowed to touch.
 *
 * Discovery and execution both run through here, so a tool that `tools/list`
 * withholds is a tool `tools/call` refuses. That equivalence is the point:
 * anything else lets a client call what it was never offered.
 */
import type { SpecToolDecision } from '@parcel/types';
import {
  getNativeTool,
  nativeToolsForSurface,
  resolveNativeToolEffect,
} from './native-registry.js';
import type { NativeToolDefinition, ParcelMcpScope, ToolDescriptor } from './types.js';

export interface ParcelMcpGrantContext {
  scopes: ReadonlySet<ParcelMcpScope>;
  plan: 'free' | 'starter' | 'pro';
  role: 'member' | 'admin' | 'owner';
  membershipActive: boolean;
  /**
   * Effective per-tool policy for this member, from the API authorization
   * context. Absent means "policy unknown", and an unknown policy is treated
   * as the registry floor rather than as permission. Native tools only.
   */
  policy?: ReadonlyMap<string, SpecToolDecision>;
  /**
   * The workspace's skill kill switches. Absent means both off, so a caller
   * that never resolved them reaches no skill tool.
   */
  skillCapabilities?: { loading: boolean; writes: boolean };
}

const ROLE_RANK: Readonly<Record<'member' | 'admin' | 'owner', number>> = {
  member: 0,
  admin: 1,
  owner: 2,
};

function roleSatisfies(actual: ParcelMcpGrantContext['role'], required: string): boolean {
  const needed = ROLE_RANK[required as keyof typeof ROLE_RANK];
  if (needed === undefined) return false;
  return ROLE_RANK[actual] >= needed;
}

export function planSatisfies(
  actual: ParcelMcpGrantContext['plan'],
  required: 'pro' | undefined,
): boolean {
  if (!required) return true;
  return actual === 'pro';
}

function policyFor(grant: ParcelMcpGrantContext, tool: NativeToolDefinition): SpecToolDecision {
  return grant.policy?.get(tool.descriptor.name) ?? tool.registryFloor;
}

/**
 * Tool-level eligibility, which is what discovery answers. The dynamic
 * classifier is deliberately not consulted: with no arguments it resolves to
 * its worst case, which would hide a multiplexed tool whose ordinary
 * operations are perfectly runnable.
 */
export function canGrantListTool(
  grant: ParcelMcpGrantContext,
  tool: NativeToolDefinition,
): boolean {
  // A Spec-only tool is not part of the public contract, whatever policy says.
  if (!tool.surfaces.includes('mcp')) return false;
  if (!grant.membershipActive) return false;
  if (!planSatisfies(grant.plan, tool.requiredPlan)) return false;
  if (!roleSatisfies(grant.role, tool.requiredRole)) return false;
  if (!grant.scopes.has(tool.requiredScope)) return false;
  return policyFor(grant, tool) !== 'blocked';
}

/**
 * Call-level authorization. Everything discovery checks, plus the action-level
 * guard, so a multiplexed tool cannot smuggle a blocked sub-operation through
 * a name whose tool-level posture is permissive.
 */
export function canGrantCallTool(
  grant: ParcelMcpGrantContext,
  tool: NativeToolDefinition,
  args: Readonly<Record<string, unknown>> = {},
): boolean {
  if (!canGrantListTool(grant, tool)) return false;
  const effect = resolveNativeToolEffect(tool, args);
  return effect !== 'destructive' && effect !== 'admin_security';
}

/** Native tools only. MCP concatenates this with listSkillToolsForGrant. */
export function listToolsForGrant(grant: ParcelMcpGrantContext): readonly ToolDescriptor[] {
  return nativeToolsForSurface('mcp')
    .filter((tool) => canGrantListTool(grant, tool))
    .map((tool) => tool.descriptor);
}

export function canGrantCallToolByName(
  grant: ParcelMcpGrantContext,
  name: string,
  args: Readonly<Record<string, unknown>> = {},
): boolean {
  const tool = getNativeTool(name);
  return tool ? canGrantCallTool(grant, tool, args) : false;
}

/**
 * Scope eligibility.
 *
 * Scopes are derived, not chosen. A member does not tick boxes at connect
 * time: what they can do is decided once, in Settings, by their workspace's
 * AI tool policy, and that policy narrows every call live. Pinning a second,
 * frozen choice onto the connection would mean loosening a setting later still
 * left the connection stuck, and an unticked box would leave a half-working
 * integration with nothing in Parcel to explain why.
 *
 * `hidden` scopes are still advertised and still storable, so a later release
 * can light them up without forcing every client to re-consent.
 */
export interface McpScopeGroup {
  scope: ParcelMcpScope;
  /** Owner/admin only. */
  requiresManager: boolean;
  /** Maps to no callable tool yet, so it is never granted. */
  hidden: boolean;
}

export const MCP_SCOPE_GROUPS: readonly McpScopeGroup[] = [
  { scope: 'data:read', requiresManager: false, hidden: false },
  { scope: 'workspace:read', requiresManager: false, hidden: false },
  { scope: 'workspace:write', requiresManager: false, hidden: false },
  // Every member and invitation tool sits at a blocked floor, so this grants
  // nothing today.
  { scope: 'workspace:admin', requiresManager: true, hidden: true },
  { scope: 'skills:read', requiresManager: false, hidden: false },
  { scope: 'skills:write', requiresManager: false, hidden: false },
  { scope: 'knowledge:read', requiresManager: false, hidden: false },
  { scope: 'knowledge:write', requiresManager: false, hidden: false },
];

const SCOPE_BY_NAME = new Map(MCP_SCOPE_GROUPS.map((group) => [group.scope as string, group]));

export function isParcelMcpScope(value: string): value is ParcelMcpScope {
  return SCOPE_BY_NAME.has(value);
}

/**
 * What this connection is granted.
 *
 * Everything the member is eligible for, unless the client explicitly asked
 * for less. Honoring a narrow request costs nothing and respects a client that
 * deliberately wants read-only; the overwhelming majority ask for nothing at
 * all, and those get the full eligible set with the policy layer doing the
 * narrowing on every call.
 */
export function deriveGrantedScopes(opts: {
  requested: readonly string[];
  role: ParcelMcpGrantContext['role'];
}): ParcelMcpScope[] {
  const requested = opts.requested.filter(isParcelMcpScope);
  const isManager = opts.role === 'owner' || opts.role === 'admin';
  return MCP_SCOPE_GROUPS.filter((group) => {
    if (group.hidden) return false;
    if (group.requiresManager && !isManager) return false;
    if (requested.length > 0 && !requested.includes(group.scope)) return false;
    return true;
  }).map((group) => group.scope);
}
